import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Car Series",
    description:
      "Car Series is a detailed brick-built motorhome designed for collectors, automotive enthusiasts, and experienced builders. With 911+ pieces, a distinctive white and orange exterior, and a wide range of functional elements, this model combines the character of an expedition vehicle with the comfort and practicality of a compact recreational vehicle.\n\nThe model features a spacious, box-shaped body with large transparent windows, a detailed front cabin, dual rear axles, exterior storage compartments, roof equipment, and a prominent roof-mounted solar panel. The clean white finish with orange accent lines gives the vehicle a modern recreational and service-vehicle appearance.\n\nFunctionality is one of the key highlights of the build. The side door can be opened, while several exterior sections can be opened or removed to reveal additional details. The model also includes a rear towing hook, making it possible to connect a compatible trailer or accessory. Adjustable stabilizing supports and other exterior mechanisms add further realism to the finished construction.\n\nThe modular design allows the main body and roof sections to be separated, giving access to the interior and making individual components easier to inspect. The model also includes a detailed cabin and living area with furniture and equipment, while the roof, side walls, and exterior storage elements provide plenty of visual detail from every angle.\n\nAt approximately 30 × 11 × 10 cm, the completed vehicle has a substantial presence while remaining easy to display. With its combination of functionality, modular construction, and detailed recreational-vehicle design, Car Series is an engaging building project and an attractive addition to any automotive brick collection.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 95028",
      "Model: Car Series",
      "Pieces: 911+",
      "Recommended Age: 14+",
      "Dimensions: approx. 30 × 11 × 10 cm",
      "Opening side door",
      "Functional rear towing hook",
      "Opening and removable body sections",
      "Modular construction",
      "Roof-mounted solar panel",
      "Retractable side awning",
      "Stabilizing support legs",
      "Detailed driver cabin",
      "Detailed interior and living area",
      "Exterior storage compartments",
      "Dual rear axle",
      "Designed for building, display, and collection",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 95028</li>",
      "<li><strong>Model:</strong> Car Series</li>",
      "<li><strong>Pieces:</strong> 911+</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
      "<li><strong>Dimensions:</strong> approx. 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Background",
    background:
      "The design of this model draws inspiration from modern motorhomes, expedition vehicles, and compact recreational vehicles designed for extended travel. These vehicles typically combine road-going mobility with integrated living and storage space, allowing the occupants to travel while carrying essential equipment and supplies.\n\nA characteristic feature of this type of vehicle is the emphasis on efficient use of space. Storage compartments, roof equipment, external accessories, and deployable elements are all integrated into the bodywork. The model translates these practical design principles into a compact brick-built format.\n\nThe inclusion of a roof-mounted solar panel and retractable awning reinforces the recreational and self-sufficient character of the vehicle. Together with the towing hook, stabilizing supports, and opening sections, these features give the model a more complete interpretation of a travelling vehicle rather than a purely static display piece.",
  },
  pl: {
    title: "Car Series",
    description:
      "Car Series to szczegółowy model kampera wykonany z klocków konstrukcyjnych, przeznaczony dla kolekcjonerów, miłośników motoryzacji oraz bardziej doświadczonych konstruktorów. Zestaw składa się z ponad 911 elementów i łączy charakterystyczną zabudowę pojazdu turystycznego z licznymi funkcjami oraz modułową konstrukcją.\n\nModel ma przestronne, pudełkowe nadwozie z dużymi przezroczystymi szybami, szczegółowo wykonanym kokpitem, dwiema tylnymi osiami, zewnętrznymi schowkami, wyposażeniem dachowym oraz widocznym panelem solarnym zamontowanym na dachu. Białe nadwozie z pomarańczowymi akcentami nadaje konstrukcji nowoczesny wygląd.\n\nDużą zaletą modelu są jego funkcje. Boczne drzwi można otwierać, a wybrane elementy zabudowy można otwierać lub zdejmować, aby lepiej zobaczyć konstrukcję. Z tyłu znajduje się również hak holowniczy, do którego można podłączyć kompatybilną przyczepę lub akcesorium. Dodatkowe podpory stabilizujące zwiększają realizm gotowego modelu.\n\nModułowa konstrukcja umożliwia oddzielanie głównych sekcji nadwozia i dachu, zapewniając łatwiejszy dostęp do wnętrza. W środku znajdziemy szczegółowo wykonaną kabinę oraz część mieszkalną z wyposażeniem. Liczne detale dachu, ścian bocznych i zewnętrznych schowków sprawiają, że model prezentuje się ciekawie z każdej strony.\n\nPo złożeniu model ma około 30 × 11 × 10 cm, dzięki czemu jest wystarczająco duży, aby zapewnić efektowną ekspozycję, a jednocześnie pozostaje wygodny do ustawienia na półce. Car Series to interesujący projekt konstrukcyjny i ciekawy dodatek do kolekcji modeli motoryzacyjnych.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 95028",
      "Model: Car Series",
      "Liczba elementów: 911+",
      "Wiek: 14+",
      "Wymiary: ok. 30 × 11 × 10 cm",
      "Otwierane boczne drzwi",
      "Funkcjonalny tylny hak holowniczy",
      "Otwierane i zdejmowane elementy zabudowy",
      "Konstrukcja modułowa",
      "Panel solarny na dachu",
      "Rozkładana markiza boczna",
      "Podpory stabilizujące",
      "Szczegółowa kabina kierowcy",
      "Szczegółowe wnętrze",
      "Zewnętrzne schowki",
      "Dwie tylne osie",
      "Idealny do budowania, ekspozycji i kolekcjonowania",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 95028</li>",
      "<li><strong>Model:</strong> Car Series</li>",
      "<li><strong>Liczba elementów:</strong> 911+</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
      "<li><strong>Wymiary:</strong> ok. 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Tło konstrukcji",
    background:
      "Stylistyka modelu nawiązuje do współczesnych kamperów, pojazdów wyprawowych oraz kompaktowych pojazdów rekreacyjnych przeznaczonych do dłuższych podróży. Konstrukcje tego typu łączą możliwość przemieszczania się z przestrzenią mieszkalną i miejscem na przechowywanie wyposażenia.\n\nCharakterystyczne jest w nich maksymalne wykorzystanie dostępnej przestrzeni. Schowki, wyposażenie dachowe, elementy montowane na zewnątrz oraz rozkładane komponenty są zintegrowane z nadwoziem, tworząc praktyczną i funkcjonalną całość.\n\nPanel solarny oraz rozkładana markiza dodatkowo podkreślają turystyczny charakter modelu. W połączeniu z hakiem holowniczym, podporami stabilizującymi i otwieranymi elementami sprawiają, że konstrukcja przypomina kompletnie wyposażony pojazd turystyczny, a nie tylko statyczny model do ekspozycji.",
  },
  de: {
    title: "Car Series",
    description:
      "Car Series ist ein detailliertes Klemmbausteinmodell eines modernen Reisemobils für Sammler, Autoenthusiasten und erfahrene Modellbauer. Mit mehr als 911 Teilen, einer markanten weiß-orangen Farbgebung und zahlreichen funktionalen Elementen verbindet das Modell die Optik eines Expeditionsfahrzeugs mit den typischen Merkmalen eines kompakten Wohnmobils.\n\nDer Aufbau besitzt große transparente Fenster, eine detaillierte Fahrerkabine, zwei Hinterachsen, seitliche Stauraumlösungen, umfangreiche Außenausstattung und ein Solarmodul auf dem Dach. Die Kombination aus weißer Karosserie und orangefarbenen Linien verleiht dem Modell einen modernen und unverwechselbaren Charakter.\n\nZu den Highlights gehören die öffnende Seitentür sowie mehrere zu öffnende oder abnehmbare Bereiche der Karosserie. Am Heck befindet sich außerdem ein funktionaler Abschlepphaken, an dem ein passender Anhänger oder ein Zubehörteil angebracht werden kann. Zusätzliche Abstützungen erhöhen den realistischen Eindruck des Fahrzeugs.\n\nDank der modularen Bauweise lassen sich größere Bereiche des Aufbaus und des Daches abnehmen. Dadurch wird der Zugang zum Innenraum erleichtert. Die Konstruktion umfasst eine detaillierte Fahrerkabine und einen ausgestatteten Wohnbereich, während die zahlreichen Außen- und Dachelemente den Gesamteindruck abrunden.\n\nMit Abmessungen von etwa 30 × 11 × 10 cm besitzt das fertige Modell eine überzeugende Präsenz und lässt sich gleichzeitig problemlos ausstellen. Car Series ist damit ein interessantes Bauprojekt und eine attraktive Ergänzung für jede Fahrzeugsammlung.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 95028",
      "Modell: Car Series",
      "Teile: 911+",
      "Altersempfehlung: 14+",
      "Abmessungen: ca. 30 × 11 × 10 cm",
      "Öffnende Seitentür",
      "Funktionaler Abschlepphaken",
      "Öffnende und abnehmbare Aufbaubereiche",
      "Modulare Konstruktion",
      "Solarmodul auf dem Dach",
      "Ausziehbare Seitenmarkise",
      "Stützbeine",
      "Detaillierte Fahrerkabine",
      "Detaillierter Innenraum",
      "Seitliche Stauraumfächer",
      "Zwei Hinterachsen",
      "Für Bau, Ausstellung und Sammlung geeignet",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95028</li>",
      "<li><strong>Modell:</strong> Car Series</li>",
      "<li><strong>Teile:</strong> 911+</li>",
      "<li><strong>Altersempfehlung:</strong> 14+</li>",
      "<li><strong>Abmessungen:</strong> ca. 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Design-Hintergrund",
    background:
      "Das Design orientiert sich an modernen Wohnmobilen, Expeditionsfahrzeugen und kompakten Freizeitfahrzeugen für längere Reisen. Solche Fahrzeuge verbinden Mobilität mit integriertem Wohnraum und zusätzlichem Stauraum.\n\nEin wichtiges Merkmal dieser Fahrzeugklasse ist die effiziente Nutzung des vorhandenen Platzes. Außenliegende Stauraumlösungen, Dachausstattung, ausklappbare Komponenten und zusätzliche Ausrüstung werden direkt in die Karosserie integriert.\n\nDas Solarmodul und die ausziehbare Markise verstärken den Charakter eines unabhängigen Reisefahrzeugs. In Verbindung mit Abschlepphaken, Stützbeinen und den verschiedenen öffnenden Bereichen entsteht ein vielseitiges Modell mit ausgeprägtem Reise- und Expeditionscharakter.",
  },
  fr: {
    title: "Car Series",
    description:
      "Car Series est un modèle en briques détaillé représentant un camping-car moderne, conçu pour les collectionneurs, les passionnés d'automobile et les constructeurs expérimentés. Avec plus de 911 pièces, une carrosserie blanche et orange et de nombreuses fonctions, il associe l'apparence d'un véhicule d'expédition à celle d'un véhicule de loisirs compact.\n\nLe modèle présente une carrosserie spacieuse, de grandes fenêtres transparentes, une cabine détaillée, deux essieux arrière, plusieurs espaces de rangement extérieurs et un panneau solaire installé sur le toit.\n\nLa fonctionnalité occupe une place importante. La porte latérale peut être ouverte, tandis que plusieurs sections de la carrosserie peuvent être ouvertes ou retirées. À l'arrière, un crochet de remorquage fonctionnel permet de connecter un accessoire ou une remorque compatible. Des béquilles stabilisatrices complètent l'ensemble.\n\nLa construction modulaire permet de retirer certaines parties principales du toit et de la carrosserie afin d'accéder plus facilement à l'intérieur. Le modèle comprend une cabine détaillée ainsi qu'un espace de vie aménagé, tandis que les équipements extérieurs et les nombreux détails renforcent son réalisme.\n\nAvec des dimensions d'environ 30 × 11 × 10 cm, le modèle offre une excellente présence sur une étagère tout en restant facile à exposer. Car Series constitue un projet de construction intéressant pour les amateurs de véhicules en briques.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 95028",
      "Modèle : Car Series",
      "Nombre de pièces : 911+",
      "Âge recommandé : 14+",
      "Dimensions : env. 30 × 11 × 10 cm",
      "Porte latérale ouvrante",
      "Crochet de remorquage fonctionnel",
      "Éléments de carrosserie ouvrants et amovibles",
      "Construction modulaire",
      "Panneau solaire sur le toit",
      "Auvent latéral déployable",
      "Béquilles stabilisatrices",
      "Cabine détaillée",
      "Intérieur détaillé",
      "Compartiments de rangement extérieurs",
      "Deux essieux arrière",
      "Idéal pour construction, exposition et collection",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 95028</li>",
      "<li><strong>Modèle :</strong> Car Series</li>",
      "<li><strong>Nombre de pièces :</strong> 911+</li>",
      "<li><strong>Âge recommandé :</strong> 14+</li>",
      "<li><strong>Dimensions :</strong> env. 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Inspiration du design",
    background:
      "Le design s'inspire des camping-cars modernes, des véhicules d'expédition et des véhicules de loisirs compacts conçus pour les longs voyages. Ces véhicules combinent mobilité, espace de vie intégré et solutions de rangement.\n\nL'utilisation efficace de l'espace constitue un élément essentiel de leur conception. Les compartiments extérieurs, l'équipement de toit et les éléments déployables sont intégrés à la carrosserie afin de maximiser la fonctionnalité.\n\nLe panneau solaire et l'auvent renforcent l'image d'un véhicule destiné aux voyages autonomes. Associés au crochet de remorquage, aux béquilles et aux différentes parties ouvrantes, ils donnent au modèle un caractère particulièrement complet.",
  },
  es: {
    title: "Car Series",
    description:
      "Car Series es un detallado modelo de bloques que representa una autocaravana moderna, pensado para coleccionistas, aficionados al automóvil y constructores experimentados. Con más de 911 piezas, una carrocería blanca y naranja y numerosos elementos funcionales, combina el carácter de un vehículo de expedición con el de un vehículo recreativo compacto.\n\nEl modelo incorpora grandes ventanas transparentes, una cabina detallada, dos ejes traseros, compartimentos de almacenamiento exteriores y un panel solar instalado en el techo. La combinación de blanco, naranja y elementos negros proporciona una estética moderna y técnica.\n\nEntre sus principales funciones se encuentran la puerta lateral practicable y varias secciones de la carrocería que pueden abrirse o desmontarse. En la parte trasera también se encuentra un gancho de remolque funcional para conectar un remolque o accesorio compatible. Las patas estabilizadoras añaden más detalle a la construcción.\n\nEl diseño modular permite retirar determinadas secciones del techo y de la carrocería para acceder mejor al interior. El modelo cuenta con una cabina detallada y una zona habitable equipada, además de numerosos elementos exteriores que pueden observarse desde diferentes ángulos.\n\nCon unas dimensiones aproximadas de 30 × 11 × 10 cm, el modelo ofrece una excelente presencia para exposición. Car Series es una propuesta atractiva para quienes disfrutan construyendo y coleccionando vehículos de bloques.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 95028",
      "Modelo: Car Series",
      "Piezas: 911+",
      "Edad recomendada: 14+",
      "Dimensiones: aprox. 30 × 11 × 10 cm",
      "Puerta lateral practicable",
      "Gancho de remolque funcional",
      "Elementos de carrocería desmontables y practicables",
      "Construcción modular",
      "Panel solar en el techo",
      "Toldo lateral desplegable",
      "Patas estabilizadoras",
      "Cabina detallada",
      "Interior detallado",
      "Compartimentos de almacenamiento exteriores",
      "Dos ejes traseros",
      "Ideal para construcción, exposición y colección",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 95028</li>",
      "<li><strong>Modelo:</strong> Car Series</li>",
      "<li><strong>Piezas:</strong> 911+</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
      "<li><strong>Dimensiones:</strong> aprox. 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Inspiración del diseño",
    background:
      "El diseño está inspirado en autocaravanas modernas, vehículos de expedición y vehículos recreativos compactos destinados a viajes prolongados. Este tipo de vehículos combina movilidad, espacio habitable y capacidad de almacenamiento.\n\nUno de sus principales objetivos es aprovechar eficazmente el espacio disponible. Los compartimentos exteriores, el equipamiento del techo y los elementos desplegables están integrados en la carrocería para aumentar la funcionalidad.\n\nEl panel solar y el toldo lateral refuerzan su carácter de vehículo preparado para viajar. Junto con el gancho de remolque, las patas estabilizadoras y las partes practicables, aportan una sensación de vehículo recreativo completo.",
  },
  nl: {
    title: "Car Series",
    description:
      "Car Series is een gedetailleerd bouwstenenmodel van een moderne camper, ontworpen voor verzamelaars, autoliefhebbers en ervaren bouwers. Met 911+ onderdelen, een opvallende wit-oranje carrosserie en verschillende functionele elementen combineert het model het karakter van een expeditievoertuig met dat van een compacte camper.\n\nHet voertuig heeft grote transparante ramen, een gedetailleerde cabine, twee achterassen, externe opbergvakken en een zonnepaneel op het dak. De combinatie van wit, oranje en zwarte technische details geeft het model een moderne uitstraling.\n\nDe functionaliteit is een belangrijk onderdeel van de constructie. De zijdeur kan worden geopend, terwijl verschillende carrosseriedelen kunnen worden geopend of verwijderd. Aan de achterkant bevindt zich bovendien een functionele trekhaak waarmee een geschikte aanhanger of accessoire kan worden gekoppeld. Stabilisatiepoten maken het ontwerp compleet.\n\nDoor de modulaire constructie kunnen delen van het dak en de carrosserie worden verwijderd om het interieur beter zichtbaar te maken. Het model beschikt over een gedetailleerde cabine en een ingerichte leefruimte, aangevuld met diverse externe accessoires.\n\nMet afmetingen van ongeveer 30 × 11 × 10 cm heeft het model een mooie aanwezigheid in een vitrinekast of op een plank. Car Series is een interessant bouwproject voor liefhebbers van voertuigmodellen.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 95028",
      "Model: Car Series",
      "Onderdelen: 911+",
      "Aanbevolen leeftijd: 14+",
      "Afmetingen: ca. 30 × 11 × 10 cm",
      "Openende zijdeur",
      "Functionele trekhaak",
      "Openende en verwijderbare carrosseriedelen",
      "Modulaire constructie",
      "Zonnepaneel op het dak",
      "Uitschuifbare zijluifel",
      "Stabilisatiepoten",
      "Gedetailleerde cabine",
      "Gedetailleerd interieur",
      "Externe opbergvakken",
      "Twee achterassen",
      "Geschikt voor bouwen, display en collectie",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95028</li>",
      "<li><strong>Model:</strong> Car Series</li>",
      "<li><strong>Onderdelen:</strong> 911+</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
      "<li><strong>Afmetingen:</strong> ca. 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Ontwerpinspiratie",
    background:
      "Het ontwerp is geïnspireerd op moderne campers, expeditievoertuigen en compacte recreatievoertuigen voor langere reizen. Deze voertuigen combineren mobiliteit met een geïntegreerde leefruimte en praktische opslagmogelijkheden.\n\nEfficiënt gebruik van de beschikbare ruimte is een belangrijk onderdeel van dit voertuigtype. Opbergvakken, dakuitrusting en uitklapbare elementen worden in de carrosserie geïntegreerd om zoveel mogelijk functionaliteit te bieden.\n\nHet zonnepaneel en de uitschuifbare luifel versterken het karakter van een reisvoertuig. Samen met de trekhaak, stabilisatiepoten en verschillende openende onderdelen zorgen ze voor een complete en herkenbare camperuitstraling.",
  },
  it: {
    title: "Car Series",
    description:
      "Car Series è un dettagliato modello in mattoncini di un moderno camper, progettato per collezionisti, appassionati di automobili e costruttori esperti. Con 911+ pezzi, una carrozzeria bianca e arancione e numerosi elementi funzionali, il modello combina lo stile di un veicolo da spedizione con quello di un compatto veicolo ricreativo.\n\nIl modello presenta ampi finestrini trasparenti, una cabina dettagliata, due assi posteriori, vani di stoccaggio esterni e un pannello solare montato sul tetto.\n\nLa funzionalità è uno degli elementi principali della costruzione. La porta laterale può essere aperta, mentre diverse sezioni della carrozzeria possono essere aperte o rimosse. Sul retro è presente anche un gancio di traino funzionale per collegare un rimorchio o un accessorio compatibile.\n\nLa struttura modulare permette di rimuovere alcune parti del tetto e della carrozzeria per accedere più facilmente agli interni. Il modello comprende una cabina dettagliata e una zona abitativa attrezzata, oltre a numerosi elementi esterni.\n\nCon dimensioni di circa 30 × 11 × 10 cm, il modello offre un'ottima presenza per l'esposizione. Car Series è un interessante progetto di costruzione per gli appassionati di veicoli in mattoncini.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 95028",
      "Modello: Car Series",
      "Pezzi: 911+",
      "Età consigliata: 14+",
      "Dimensioni: circa 30 × 11 × 10 cm",
      "Portiera laterale apribile",
      "Gancio di traino funzionale",
      "Elementi della carrozzeria apribili e removibili",
      "Costruzione modulare",
      "Pannello solare sul tetto",
      "Tenda laterale estensibile",
      "Piedini stabilizzatori",
      "Cabina dettagliata",
      "Interni dettagliati",
      "Vani di stoccaggio esterni",
      "Due assi posteriori",
      "Ideale per costruzione, esposizione e collezione",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 95028</li>",
      "<li><strong>Modello:</strong> Car Series</li>",
      "<li><strong>Pezzi:</strong> 911+</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
      "<li><strong>Dimensioni:</strong> circa 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "Ispirazione del design",
    background:
      "Il design si ispira ai moderni camper, ai veicoli da spedizione e ai veicoli ricreativi compatti progettati per i viaggi più lunghi. Questi mezzi combinano mobilità, spazio abitativo e capacità di stoccaggio.\n\nL'utilizzo efficiente dello spazio è uno degli aspetti più importanti di questa tipologia di veicolo. Vani esterni, equipaggiamento sul tetto ed elementi apribili sono integrati nella carrozzeria per migliorare la funzionalità.\n\nIl pannello solare e la tenda laterale sottolineano il carattere turistico del veicolo. Insieme al gancio di traino, ai piedini stabilizzatori e alle varie sezioni apribili, rendono il modello particolarmente completo.",
  },
  ru: {
    title: "Car Series",
    description:
      "Car Series — это детализированная модель современного автодома из конструктора, созданная для коллекционеров, любителей автомобилей и опытных конструкторов. Набор состоит из 911+ деталей и сочетает бело-оранжевый дизайн с большим количеством функциональных элементов.\n\nМодель получила просторный кузов, большие прозрачные окна, детализированную кабину, две задние оси, внешние отделения для хранения и солнечную панель на крыше.\n\nОдной из главных особенностей является функциональность. Боковую дверь можно открывать, а отдельные части кузова можно открывать или снимать. В задней части расположен функциональный буксировочный крюк, позволяющий подключить совместимый прицеп или аксессуар.\n\nМодульная конструкция позволяет снимать части крыши и кузова, открывая доступ к интерьеру. Внутри представлены детализированная кабина и жилая зона с оборудованием. Дополнительные внешние элементы делают модель интересной для рассмотрения со всех сторон.\n\nРазмер готовой модели составляет около 30 × 11 × 10 см, благодаря чему она хорошо подходит для экспонирования. Car Series станет интересным проектом для любителей автомобильных моделей из конструктора.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 95028",
      "Модель: Car Series",
      "Количество деталей: 911+",
      "Рекомендуемый возраст: 14+",
      "Размеры: примерно 30 × 11 × 10 см",
      "Открывающаяся боковая дверь",
      "Функциональный буксировочный крюк",
      "Открывающиеся и съёмные элементы кузова",
      "Модульная конструкция",
      "Солнечная панель на крыше",
      "Выдвижной боковой тент",
      "Стабилизирующие опоры",
      "Детализированная кабина",
      "Детализированный интерьер",
      "Внешние отделения для хранения",
      "Две задние оси",
      "Подходит для сборки, экспонирования и коллекционирования",
    ],
    specsTitle: "Спецификация",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 95028</li>",
      "<li><strong>Модель:</strong> Car Series</li>",
      "<li><strong>Количество деталей:</strong> 911+</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
      "<li><strong>Размеры:</strong> примерно 30 × 11 × 10 см</li>",
    ],
    backgroundTitle: "Особенности дизайна",
    background:
      "Дизайн модели вдохновлён современными автодомами, экспедиционными машинами и компактными туристическими автомобилями для длительных путешествий. Такие транспортные средства объединяют мобильность с жилым пространством и системами хранения.\n\nОдним из ключевых принципов является рациональное использование доступного пространства. Внешние багажные отделения, оборудование на крыше и раскладывающиеся элементы интегрируются в кузов автомобиля.\n\nСолнечная панель и выдвижной тент подчёркивают туристический характер модели. Вместе с буксировочным крюком, стабилизирующими опорами и открывающимися элементами они создают полноценный образ современного автодома.",
  },
  zh: {
    title: "Car Series",
    description:
      "Car Series 是一款现代房车积木模型，专为收藏家、汽车爱好者以及经验丰富的玩家设计。整套包含 911+ 个积木零件，采用白色与橙色车身设计，并加入了丰富的功能结构。\n\n模型拥有宽大的车身、大面积透明车窗、精细驾驶舱、双后桥、外部储物空间以及安装在车顶的太阳能板，整体造型具有明显的现代房车与旅行车辆特色。\n\n模型的一大亮点是功能设计。侧门可以开启，车身部分结构也可以打开或拆卸。车尾配备了可用于连接兼容拖车或配件的拖钩结构，同时还设计了稳定支撑脚。\n\n模块化结构允许玩家拆卸部分车顶和车身区域，从而更方便地观察内部。模型内部还还原了驾驶舱以及生活区域，并配备多种室内及外部细节。\n\n成品尺寸约为 30 × 11 × 10 cm，具有不错的展示效果，同时也比较容易摆放在收藏架上。Car Series 是一款兼具拼搭乐趣、细节和展示价值的汽车主题积木模型。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 95028",
      "型号： Car Series",
      "积木数量： 911+ 块",
      "建议年龄： 14 岁以上",
      "成品尺寸： 约 30 × 11 × 10 cm",
      "侧门可开启",
      "尾部拖钩可连接",
      "部分车身结构可开启或拆卸",
      "模块化设计",
      "车顶太阳能板",
      "可展开侧边遮阳篷",
      "稳定支撑脚",
      "精细驾驶舱",
      "精细内部空间",
      "外部储物空间",
      "双后桥",
      "适合拼搭、展示与收藏",
    ],
    specsTitle: "规格",
    specs: [
      "<li><strong>品牌：</strong>12GO</li>",
      "<li><strong>产品编号：</strong>95028</li>",
      "<li><strong>型号：</strong>Car Series</li>",
      "<li><strong>积木数量：</strong>911+ 块</li>",
      "<li><strong>建议年龄：</strong>14 岁以上</li>",
      "<li><strong>成品尺寸：</strong>约 30 × 11 × 10 cm</li>",
    ],
    backgroundTitle: "设计背景",
    background:
      "这款模型的设计灵感来源于现代房车、探险车辆以及适合长途旅行的紧凑型休闲车辆。这类车辆通常将道路行驶能力、生活空间和储物功能结合在一个车身结构中。\n\n合理利用空间是这类车辆的重要设计特点。外部储物空间、车顶装备以及可展开结构通常都会被整合到车身之中，以提高车辆的实用性。\n\n车顶太阳能板和侧边遮阳篷进一步强化了旅行房车的定位。配合拖钩、稳定支撑脚以及可开启结构，使整个模型呈现出更加完整的现代旅行车辆形象。",
  },
};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}

<h2>${c.backgroundTitle}</h2>

${paragraphs(c.background)}`;
}

export const RICH_PRODUCT_COPY_95028: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
